import { useState } from 'react';
import type { ContentItem } from '../../../models/ContentManagementInterface';
import type BackendService from '../../../services/admin/BackendService';
import type { DocumentAsset } from '../../../services/admin/adminBackendServiceDocumentLibraryMixin';
import type { Box as CropBox } from './cropAdjustBoxes';
import type { DocumentImportScopeMode } from './DocumentImportScopeBar';
import type { ImageCardsCropMeta } from './useImageCardsSplit';
import { MAX_IMPORT_PAGES, nextBatchStart, type ImportedPageRange } from './documentImportLimits';

/**
 * `resumeAfter` is the last batch already imported from this document — when pages remain, the
 * scope opens on the next batch so a long document carries on where it stopped.
 */
export function useDocumentLibraryImport(
  pageCount: number,
  resumeAfter?: ImportedPageRange | null,
) {
  const resumeFrom = nextBatchStart(resumeAfter, pageCount);
  const [scopeMode, setScopeMode] = useState<DocumentImportScopeMode>(
    resumeFrom ? 'range' : 'page',
  );
  const [fromPage, setFromPage] = useState(resumeFrom ?? 1);
  const [toPage, setToPage] = useState(
    resumeFrom ? Math.min(resumeFrom + MAX_IMPORT_PAGES - 1, pageCount) : 1,
  );

  const clamp = (page: number) => Math.min(Math.max(page, 1), Math.max(pageCount, 1));

  const setRangeFrom = (page: number) => {
    const next = clamp(page);
    setFromPage(next);
    setToPage((current) =>
      Math.min(Math.max(current, next), next + MAX_IMPORT_PAGES - 1, pageCount),
    );
  };

  const setRangeTo = (page: number) =>
    setToPage(Math.min(Math.max(clamp(page), fromPage), fromPage + MAX_IMPORT_PAGES - 1));

  const selectNextBatch = (lastPage: number, setCurrentPage: (n: number) => void) => {
    const next = lastPage + 1;
    if (next > pageCount) return false;
    setScopeMode('range');
    setFromPage(next);
    setToPage(Math.min(next + MAX_IMPORT_PAGES - 1, pageCount));
    setCurrentPage(next);
    return true;
  };

  const selectPage = (page: number, setCurrentPage: (n: number) => void) => {
    const next = clamp(page);
    setCurrentPage(next);
    if (scopeMode !== 'range' && scopeMode !== 'document') {
      setFromPage(next);
      setToPage(next);
    }
  };

  return {
    scopeMode,
    fromPage,
    toPage,
    setScopeMode,
    setFromPage: setRangeFrom,
    setToPage: setRangeTo,
    selectPage,
    selectNextBatch,
  };
}

function resolvePageRange(opts: {
  scopeMode: DocumentImportScopeMode;
  currentPage: number;
  fromPage: number;
  toPage: number;
  pageCount: number;
}): { fromPage: number; toPage: number } {
  if (opts.scopeMode === 'range') return { fromPage: opts.fromPage, toPage: opts.toPage };
  if (opts.scopeMode === 'document')
    return { fromPage: 1, toPage: Math.min(opts.pageCount, MAX_IMPORT_PAGES) };
  return { fromPage: opts.currentPage, toPage: opts.currentPage };
}

export async function importLibrarySelection(opts: {
  documentAsset: DocumentAsset;
  backendService: BackendService;
  scopeMode: DocumentImportScopeMode;
  currentPage: number;
  fromPage: number;
  toPage: number;
  pageCount: number;
  box: CropBox;
  modelType: string;
  modelId: string;
  onImported: (
    sections: ContentItem[],
    cropMeta?: ImageCardsCropMeta | null,
  ) => void | Promise<void>;
}): Promise<{ fromPage: number; toPage: number }> {
  const { fromPage, toPage } = resolvePageRange(opts);
  if (toPage < fromPage || toPage - fromPage + 1 > MAX_IMPORT_PAGES) {
    throw new Error(`Import at most ${MAX_IMPORT_PAGES} pages at a time`);
  }
  const box =
    opts.scopeMode === 'region'
      ? {
          x: Math.round(opts.box.left),
          y: Math.round(opts.box.top),
          width: Math.round(opts.box.width),
          height: Math.round(opts.box.height),
        }
      : undefined;
  const started = Date.now();
  console.log('[document-import] UI request start', {
    documentId: opts.documentAsset.externalId,
    scopeMode: opts.scopeMode,
    fromPage,
    toPage,
    box,
  });
  try {
    const result = await opts.backendService.importDocumentSelection(
      opts.documentAsset.externalId,
      {
        fromPage,
        toPage,
        box,
        modelType: opts.modelType,
        modelId: opts.modelId,
      },
    );
    console.log('[document-import] UI request done', {
      documentId: opts.documentAsset.externalId,
      ms: Date.now() - started,
      blocks: result.blocks?.length ?? 0,
      aiTier: result.aiTier,
      adjustable: result.cropSource !== undefined,
    });
    const cropMeta: ImageCardsCropMeta | null = result.cropSource
      ? {
          source: result.cropSource,
          boxes: result.cropBoxes ?? [],
          confidence: result.cropConfidence ?? 'high',
        }
      : null;
    await opts.onImported((result.blocks ?? []) as ContentItem[], cropMeta);
    return { fromPage, toPage };
  } catch (error) {
    console.error('[document-import] UI request failed', {
      documentId: opts.documentAsset.externalId,
      ms: Date.now() - started,
      error,
    });
    throw error;
  }
}
